import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthStatus } from "../components/hooks/useAuthStatus";
import AppMenu from "../components/layout/AppMenu";
import Spinner from "../components/layout/Spinner";
import { ReportsPanel, SharedReportsPanel } from "../components/layout/TeamPlayerPanels";
import AIReportsTabs from "../components/matches/AIReportsTabs";
import MatchSummary from "../components/matches/MatchSummary";
import { isAllowedEmailUser } from "../components/utils/access";
import {
    calculateDVWStats,
    generateMatch,
} from "../components/utils/DVWFile";
import {
    calculatePSVBStats,
    initWithPSVBCompressedBuffer,
} from "../components/utils/PSVBFile";
import { calculateSideoutStats } from "../components/utils/StatsItem";
import { generateUUID } from "../components/utils/Utils";
import { useLanguage } from "../context/Language/LanguageContext";

// Sample reports shown to users who don't yet have AI access. Drop the PDF
// files into public/samples/ and list them here (path is relative to the site
// root, e.g. "/samples/state-of-play-sample.pdf").
const kSampleReports = [
  {
    title: "Attack Analysis (English)",
    file: "/samples/Deep Analysis - Attack-en.pdf",
  },
  {
    title: "Blocking Analysis (Vietnamese)",
    file: "/samples/Deep Analysis - Blocking-vi.pdf",
  },
  {
    title: "Opponent Tendencies (Japanese)",
    file: "/samples/Deep Analysis - Opponent Tendencies (Scouting)-ja.pdf",
  },
];

// Builds a processed match object from a raw session buffer, mirroring the
// pipeline used by MultiSessions so the AI panels receive the same shape.
function processMatch(match) {
  let mx;
  if (match.buffer.includes("DATAVOLLEY")) {
    const m = generateMatch(match.buffer, {
      winScore: match.winScore ?? undefined,
      winByTwo: match.winByTwo ?? undefined,
    });
    mx = calculateDVWStats(m);
    mx.app = "DataVolley";
    mx = calculateSideoutStats(mx, "DataVolley");
  } else {
    const m = initWithPSVBCompressedBuffer(match.buffer);
    mx = calculatePSVBStats(m);
    mx.app = "VBStats";
    mx = calculateSideoutStats(mx, "VBStats");
  }
  mx.videoOnlineUrl = match.videoOnlineUrl;
  mx.videoStartTimeSeconds = match.videoStartTimeSeconds;
  mx.videoOffset = match.videoOffset;
  // Anchor to the KNOWN existing document id rather than trusting whatever
  // generateMatch recomputed from the raw buffer (see MultiSessions.jsx).
  if (match.id) {
    mx.guid = match.id;
  } else if (!mx.guid) {
    mx.guid = generateUUID();
  }
  return mx;
}

// The "Coming Soon" experience for users without AI access: a banner plus
// a tabbed viewer that shows the sample reports one at a time.
function ComingSoon({ t }) {
  const [active, setActive] = useState(0);
  const current = kSampleReports[active];

  return (
    <div className="flex flex-col items-center gap-6 py-8">
      <div className="alert alert-info max-w-2xl">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-6 h-6 shrink-0"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
          />
        </svg>
        <div>
          <h3 className="font-bold">{t("AI Reports — Coming Soon")}</h3>
          <p className="text-sm">
            {t(
              "AI-powered match reports and deep analysis are on the way. Here are some sample reports to show what's coming."
            )}
          </p>
        </div>
      </div>

      <div className="w-full max-w-4xl">
        <div role="tablist" className="tabs tabs-boxed flex-wrap">
          {kSampleReports.map((r, idx) => (
            <button
              key={r.file}
              type="button"
              role="tab"
              className={`tab ${active === idx ? "tab-active" : ""}`}
              onClick={() => setActive(idx)}
            >
              {t(r.title)}
            </button>
          ))}
        </div>

        <div className="card bg-base-200 shadow-sm mt-4">
          <div className="card-body gap-3">
            <h4 className="card-title text-base">{t(current.title)}</h4>
            <object
              key={current.file}
              data={current.file}
              type="application/pdf"
              className="w-full h-[70vh] rounded border border-base-300 bg-base-100"
              aria-label={current.title}
            >
              <p className="text-sm text-base-content/60 p-2">
                {t("Preview not available.")}
              </p>
            </object>
            <div className="card-actions justify-end">
              <a
                href={current.file}
                target="_blank"
                rel="noreferrer"
                className="btn btn-sm btn-primary"
              >
                {t("Open sample")}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Dedicated AI Reports page reached from the Matches list. Hosts the AI State
// of Play report and Deep AI Analysis, gated to allowed-email users; everyone
// else sees a "Coming Soon" banner with sample reports.
function AIReports() {
  const location = useLocation();
  const navigate = useNavigate();
  const { firebaseUser, checkingStatus } = useAuthStatus();
  const { t } = useLanguage();

  const state = location.state || {};
  const rawMatches = state.matches || [];
  const team = state.team || null;

  const [allMatches, setAllMatches] = useState([]);
  const [selectedGame, setSelectedGame] = useState(0);
  const [selectedTeam, setSelectedTeam] = useState(0);
  const [processing, setProcessing] = useState(true);

  const allowed = isAllowedEmailUser(firebaseUser);

  useEffect(() => {
    if (!allowed) {
      setProcessing(false);
      return;
    }
    const processed = [];
    for (const match of rawMatches) {
      if (!match || !match.buffer) {
        continue;
      }
      try {
        processed.push(processMatch(match));
      } catch (e) {
        console.log("Skipping match that failed to process", e);
      }
    }
    setAllMatches(processed);
    setProcessing(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed]);

  if (checkingStatus) {
    return <Spinner />;
  }

  return (
    <div className="mt-2">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <AppMenu />
          <h2 className="text-lg font-semibold">{t("AI Reports")}</h2>
        </div>
        <button className="btn btn-sm btn-ghost" onClick={() => navigate(-1)}>
          {t("Back")}
        </button>
      </div>

      <div className="mb-4 flex flex-col gap-3 md:flex-row">
        <div className="min-h-[8rem] flex-1">
          <ReportsPanel scope="adhoc" refId="self" reportLabel={team} t={t} />
        </div>
        <div className="min-h-[8rem] flex-1">
          <SharedReportsPanel t={t} />
        </div>
      </div>

      {!allowed ? (
        <ComingSoon t={t} />
      ) : processing ? (
        <Spinner />
      ) : allMatches.length === 0 ? (
        <p className="text-base-content/70 py-8 text-center">
          {t("No matches were loaded. Select one or more matches and try again.")}
        </p>
      ) : (
        <>
          <div className="flex justify-between">
            <MatchSummary
              matches={allMatches}
              team={team}
              gameSelected={selectedGame}
              onGameSelected={(sgn) => setSelectedGame(sgn)}
              teamSelected={selectedTeam}
              onTeamSelected={(tmn) => setSelectedTeam(tmn)}
              isLive={false}
            ></MatchSummary>
          </div>
          <div className="mt-4">
            <AIReportsTabs
              matches={allMatches}
              selectedGame={selectedGame}
              selectedTeam={selectedTeam}
            />
          </div>
        </>
      )}
    </div>
  );
}

export default AIReports;
